import React from "react";
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Flex,
  Text,
  AspectRatio,
  Box,
  type TextProps,
  type AspectRatioProps,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "ui/icons";
import { remCalc } from "theme/utils";

type GradientAccordionItemProps = {
  /**
   * Unique identifier for the accordion item.
   * Required if there are multiple item titles that are the same.
   */
  id?: string;

  /**
   * Title of the accordion item.
   */
  title: string;

  /**
   * The HTML element to render the title as.
   *
   * @default "h3"
   */
  titleAs?: TextProps["as"];

  /**
   * The description of the accordion item.
   */
  description?: string;

  /**
   * The media to render in the accordion item. Each item can have different media.
   * Can be either an image or lottie animation, but must be a single JSX Element.
   *
   * Only used in `MediaAccordion` with type `dynamic` on mobile screens.
   */
  media?: JSX.Element;

  /**
   * The aspect ratio of the media.
   *
   * @default 1 / 1
   */
  mediaRatio?: AspectRatioProps["ratio"];

  /**
   * The active gradient color of the accordion item.
   */
  activeGradientColor: string;

  /**
   * The border color of the accordion item.
   */
  inactiveBorderColor: string;

  /**
   * The button color of the accordion item.
   */
  buttonColor: string;
};

export function GradientAccordionItem({
  title,
  titleAs = "h3",
  description,
  media,
  mediaRatio,
  activeGradientColor,
  inactiveBorderColor,
  buttonColor,
}: GradientAccordionItemProps) {
  return (
    <AccordionItem border="none">
      {({ isExpanded }) => (
        <Box
          border="1px solid"
          borderColor={isExpanded ? "white" : inactiveBorderColor}
          borderRadius="smaller"
          bg={isExpanded ? activeGradientColor : "transparent"}
          transition="all 0.2s ease-in-out"
          overflow="hidden"
        >
          <Box scrollMarginTop="huge" as={titleAs} role="heading">
            <AccordionButton
              borderRadius="tinier"
              display="flex"
              gap="small"
              alignItems="start"
              justifyContent="space-between"
              textAlign="left"
              px="small"
              py="small"
              _hover={{ bg: "transparent" }}
              _expanded={{
                svg: { transform: "rotate(180deg)" },
              }}
            >
              <Flex flexDir="column" gap="tiny">
                <Text as="span" textStyle="headline3">
                  {title}
                </Text>
                {description && (
                  <Text as="span" textStyle="caption1">
                    {description}
                  </Text>
                )}
              </Flex>
              <Flex
                align="center"
                borderRadius="full"
                bg={buttonColor}
                color="new.gray.950"
                boxSize={remCalc(42)}
                flexShrink={0}
                justify="center"
              >
                <ChevronDownIcon transition="transform 0.25s ease-in-out" />
              </Flex>
            </AccordionButton>
          </Box>
          <AccordionPanel textStyle="caption1" mb="small" p={0}>
            {media && (
              <AspectRatio
                display={{ base: "block", md: "none" }}
                borderRadius="small"
                ratio={mediaRatio}
                flex={1}
                w="100%"
                maxW={{ base: "mediaMaxW.mobile" }}
                mx="auto"
                pos="relative"
                overflow="hidden"
                px="smaller"
              >
                {media}
              </AspectRatio>
            )}
          </AccordionPanel>
        </Box>
      )}
    </AccordionItem>
  );
}
